// View as: an X SCALE staff login looks at the dashboard as one of the brand's people. What the browser keeps of it
// (the person's email, for this tab), what it says, and the api with every action switched off while it lasts.
import { ConsoleApiError } from "@/api/consoleClient";
import { formatRoleLabel } from "@/components/common";

export const VIEW_AS_STORAGE_KEY = "growthos.view-as";
export const READ_ONLY_MESSAGE = "You are viewing as another person; actions are off.";
/** The answers the server gives a View as header it will not honour. Any of them ends the look. */
export const VIEW_AS_ERROR_CODES = new Set([
  "view_as_forbidden",
  "view_as_read_only",
  "view_as_invalid",
  "view_as_client_required",
  "view_as_target_not_found",
  "view_as_target_is_staff",
  "view_as_target_too_many_brands",
]);

const ROLE_NAMES = { owner: "Owner", admin: "Admin", member: "Member", viewer: "Viewer" };
// Reads keep working under View as: everything that fetches, and the downloads. Every other api method acts.
const READ_METHOD_PREFIXES = ["get", "download"];

export function viewAsRoleLabel(role) {
  const normalized = String(role ?? "").trim().toLowerCase();
  return ROLE_NAMES[normalized] ?? formatRoleLabel(normalized);
}

/** The banner's one line: "Viewing as vince@… · Owner · read only". */
export function viewAsLabel(viewingAs) {
  return `Viewing as ${viewingAs.email} · ${viewAsRoleLabel(viewingAs.role)} · read only`;
}

export function readStoredViewAs() {
  try {
    const parsed = JSON.parse(window.sessionStorage.getItem(VIEW_AS_STORAGE_KEY) ?? "null");
    const email = String(parsed?.email ?? "").trim().toLowerCase();
    return email ? { email } : null;
  } catch {
    return null;
  }
}

export function persistViewAs(target) {
  try {
    if (target?.email) window.sessionStorage.setItem(VIEW_AS_STORAGE_KEY, JSON.stringify({ email: target.email }));
    else window.sessionStorage.removeItem(VIEW_AS_STORAGE_KEY);
  } catch {
    // Session storage can be blocked. The look then lasts for this page view only, which is safe.
  }
}

export function isReadMethod(name) {
  return READ_METHOD_PREFIXES.some((prefix) => name.startsWith(prefix));
}

export function readOnlyRefusal(message = READ_ONLY_MESSAGE) {
  return new ConsoleApiError(message, { status: 403, code: "view_as_read_only" });
}

/** The api with every action replaced by a refusal that says why; reads pass through untouched. */
export function readOnlyApi(api, message = READ_ONLY_MESSAGE) {
  const guarded = {};
  for (const [name, value] of Object.entries(api)) {
    if (typeof value !== "function") guarded[name] = value;
    else if (isReadMethod(name)) guarded[name] = value;
    else guarded[name] = () => Promise.reject(readOnlyRefusal(message));
  }
  guarded.readOnly = true;
  return guarded;
}
